import dynamic from "next/dynamic"
import { useEffect, useRef } from "react"
import Checkmark from "../common/icons/checkmark"
import { Spinner } from "../common/loading/spinner"
import StepHeading from "./stepHeading"
import useCheckoutContext, { useStep, useStepLoading } from "./store"

const mapping = {
  paymentMethod: dynamic(() => import("./payment/payment"), {
    // eslint-disable-next-line react/display-name
    loading: () => (
      <div className="mx-auto">
        <Spinner />
      </div>
    )
  }),
  paymentAmount: dynamic(() => import("./amount/amount")),
  article: dynamic(() => import("./article/article")),
  userDetails: dynamic(() => import("./user-details/userDetails")),
  memorySheet: dynamic(() => import("./memory-sheet/memorySheet")),
  deliveryMethod: dynamic(() => import("./delivery-method/deliveryMethod")),
  membershipType: dynamic(() => import("./membership-type/membershipType")),
  quantity: dynamic(() => import("./quantity/quantity")),
  memberPaperDeliveryMethod: dynamic(() =>
    import("./member-paper-delivery-method/memberPaperDeliveryMethod")
  ),
  deliveryAddress: dynamic(() => import("./delivery-address/deliveryAddress")),
  recruiterId: dynamic(() => import("./recruiter-id/recruiterId")),
  giftShopCheckout: dynamic(() =>
    import("./gift-shop-checkout/giftShopCheckout")
  ),
  giftShopExtraSale: dynamic(() =>
    import("./gift-shop-extra-sale/giftShopExtraSale")
  )
}

function Step({ index, defaultValues, type, orderSessionId, formSettings }) {
  const { activeStepIndex, setActiveStepIndex } = useCheckoutContext()
  const { stepLoading } = useStepLoading()
  const { step, isLastStep } = useStep(index)
  const isPreviousStep = index < activeStepIndex
  const isActiveStep = index === activeStepIndex
  const stepRef = useRef()

  useEffect(() => {
    if (isActiveStep && index > 0) {
      const top = stepRef.current.offsetTop
      const headerHeight = document.querySelector("header").clientHeight

      window.scrollTo({
        top: top - headerHeight - 13,
        behavior: "smooth"
      })
    }

    if (isActiveStep && index > 0) {
      // stepRef.current.querySelector("input, select, textarea").focus()
    }
  }, [isActiveStep, index])

  const stepClasses =
    isPreviousStep && !stepLoading
      ? "cursor-pointer hover:text-black transition-colors duration-200 text-black-50 border-2 border-green-100"
      : !isActiveStep
      ? "cursor-not-allowed text-black-25 border-summer-green-50"
      : "border-summer-green-100"

  const SubComponent = mapping[step.name] ?? null

  if (!SubComponent) return null

  return (
    <>
      <div
        className={[
          "checkout-step relative p-5 sm:p-6 bg-white border-2 rounded mb-6",
          stepClasses,
          !isLastStep ? "mb-6" : ""
        ].join(" ")}
        onClick={() =>
          isPreviousStep && !stepLoading ? setActiveStepIndex(index) : null
        }
        ref={stepRef}>
        <StepHeader
          index={index}
          isPreviousStep={isPreviousStep}
          isActiveStep={isActiveStep}
        />

        {isActiveStep && (
          <div className="flex flex-col" id="stepContent">
            <SubComponent
              step={step}
              defaultValues={defaultValues}
              type={type}
              orderSessionId={orderSessionId}
              stepRef={stepRef}
              formSettings={formSettings}
            />
            {stepLoading && (
              <StepLoader message="Var god vänta, din betalning behandlas..." />
            )}
          </div>
        )}
      </div>
    </>
  )
}

function StepHeader({ index, isPreviousStep, isActiveStep }) {
  const {
    step: { title, description, descriptionType }
  } = useStep(index)
  const { stepLoading } = useStepLoading()

  return (
    <div className="flex items-center" id="stepHeader">
      {isPreviousStep && (
        <div className="flex items-center justify-center w-8 h-8 p-2 mr-3 bg-green-100 rounded-full step-header-checkmark">
          <Checkmark className="w-3" />
        </div>
      )}

      <StepHeading
        title={title}
        stepIndex={index}
        isActiveStep={isActiveStep}
        description={description}
        descriptionType={descriptionType}
      />

      {isPreviousStep && !stepLoading && (
        <div className="px-0 py-1 ml-auto text-sm text-black underline border-none rounded-md md:px-3 md:no-underline md:border-black md:border-2 md:border-solid md:text-base">
          Ändra
        </div>
      )}
    </div>
  )
}

function StepLoader() {
  const { stepLoaderMessage } = useStepLoading()

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-1">
      <p className="text-lg font-semibold">{stepLoaderMessage}</p>
      <Spinner className="mt-4" />
    </div>
  )
}

export default Step
